import { readRegistrationDate } from './registrationDate'
import materialDisplayName from './materialDisplayName'
import materialQuantities from './materialQuantities'

/**
 * De prijs per stuk van één regel: de prijs die bij het registreren is
 * vastgelegd (`ItemPriceAtRegistration`, T20 van
 * docs/features/huishouden-vaste-gegevens.md), zodat een latere prijswijziging
 * in de catalogus een eerdere verantwoording niet verschuift. Een regel van
 * vóór die vastlegging rekent met de huidige prijs en draagt `prijsIsHuidig`,
 * zodat een rapport kan zeggen dat het een benadering is. 0 is een echte prijs
 * (advies), dus `??`-achtig en niet `||`.
 * AI-CONTEXT: See docs/pitfalls.md#state-or-fallback-erases-zero
 *
 * @param {{ItemPriceAtRegistration?: *}} entry
 * @param {{ItemPrice?: number}|undefined} material
 * @returns {{unitPrice: number, prijsIsHuidig: boolean}}
 */
const unitPriceFor = (entry, material) => {
    const vast = entry?.ItemPriceAtRegistration
    if (typeof vast === 'number' && Number.isFinite(vast) && vast >= 0) return { unitPrice: vast, prijsIsHuidig: false }
    return { unitPrice: Number(material?.ItemPrice) || 0, prijsIsHuidig: true }
}

/**
 * Join a household's `AdressMaterials` entries to the master `Materials` list,
 * producing display rows + planned/used totals.
 *
 * Pure — no Firestore, no React. Soft-deleted entries are excluded. Rows are
 * ordered by `Position` ascending when present.
 *
 * Aantal en status komen uit materialQuantities: 'gebruikt' als dat is ingevuld,
 * anders 'mee te nemen'; een oude regel houdt zijn `Quantity` en zijn Status
 * (`'done'` → used, anders planned).
 * AI-CONTEXT: See docs/features/household-materials.md (Uitbreiding 2026-10)
 *
 * Elke entry blijft een eigen regel, ook wanneer twee entries hetzelfde
 * materiaal dragen: ze kunnen op verschillende dagen zijn geregistreerd en dus
 * in verschillende boekjaren tellen. Samenvoegen zou één datum overhouden.
 * AI-CONTEXT: See docs/plans/ondersteuningsmodule-code-integratie.md (T1)
 *
 * @param {{
 *   entries?: Array<{ID:string, docid?:string, MaterialID:string, Quantity:number, ItemPriceAtRegistration?:number, Status?:string, Description?:string, Position?:number, Deleted?:boolean|string, RegisteredAt?:*, Timestamp?:*}>,
 *   materials?: Array<{ID:string, Title?:string, Unit?:string, ItemPrice?:number}>
 * }} input
 * @returns {{ rows: Array<object>, totals: {used:number, planned:number, combined:number} }}
 */
const computeHouseholdMaterials = ({ entries = [], materials = [] }) => {
    const materialsByID = new Map()
    for (const m of materials) {
        if (m && m.ID) materialsByID.set(m.ID, m)
    }

    const live = entries
        .filter((e) => e && e.Deleted !== true && e.Deleted !== 'true')
        .slice()
        .sort((a, b) => (Number(a?.Position) || 0) - (Number(b?.Position) || 0))

    const rows = []
    let usedTotal = 0
    let plannedTotal = 0

    for (const entry of live) {
        const material = materialsByID.get(entry?.MaterialID)
        const title = materialDisplayName(material) || 'Onbekend materiaal'
        const unit = material?.Unit || ''
        const { unitPrice, prijsIsHuidig } = unitPriceFor(entry, material)
        const aantallen = materialQuantities(entry)
        const quantity = aantallen.impact
        const subtotal = unitPrice * quantity
        const status = aantallen.status === 'done' ? 'used' : 'planned'

        rows.push({
            ID: entry?.ID,
            docid: entry?.docid,
            materialID: entry?.MaterialID,
            title,
            unit,
            quantity,
            unitPrice,
            prijsIsHuidig,
            subtotal,
            status,
            description: entry?.Description || '',
            registrationDate: readRegistrationDate(entry),
        })

        if (status === 'used') usedTotal += subtotal
        else plannedTotal += subtotal
    }

    return {
        rows,
        totals: {
            used: usedTotal,
            planned: plannedTotal,
            combined: usedTotal + plannedTotal,
        },
    }
}

export { unitPriceFor }
export default computeHouseholdMaterials
